import { Fragment, useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, Ban, ShieldCheck, Building2, MessageCircle, IdCard, Lock, Plus, Trash2, Pencil } from "lucide-react";
import { printStaffWelcomeCard } from "@/lib/print";
import { loadBranches } from "@/lib/branches";
import { NAV } from "@/components/AppLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const BLOCKABLE_OPTIONS = NAV.filter((i) => !i.heading && i.to !== "/admin-panel");
const GROUPED_OPTIONS = (() => {
  const out = [];
  let heading = null;
  NAV.forEach((i) => {
    if (i.heading) { heading = i.heading; return; }
    if (i.to === "/admin-panel") return;
    const last = out[out.length - 1];
    if (!last || last.heading !== heading) out.push({ heading, items: [] });
    out[out.length - 1].items.push(i);
  });
  return out;
})();

const EMPTY_PERM = { blocked: [], allowed: [] };

function BranchOptionsDialog({ open, onClose, branches, users }) {
  const [perms, setPerms] = useState(null);
  const [userPerms, setUserPerms] = useState(null);
  const names = (branches || []).map((b) => b.branch);
  const [branch, setBranch] = useState("");
  const [userId, setUserId] = useState("");
  useEffect(() => {
    if (open) {
      api.get("/admin/branch-permissions").then((r) => setPerms(r.data || {})).catch(() => setPerms({}));
      api.get("/admin/user-permissions").then((r) => setUserPerms(r.data || {})).catch(() => setUserPerms({}));
      if (!branch) setBranch("All Branches");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, names.length]);
  const staffIds = [...new Set((users || []).filter((u) => u.role !== "admin" && u.user_id).map((u) => u.user_id))].sort();
  const entry = (obj, key) => obj?.[key] || EMPTY_PERM;

  const optState = (opt) => {
    if (userId) {
      const e = entry(userPerms, userId);
      if (opt.adminOnly) return e.allowed.includes(opt.to) ? "Allowed" : "Blocked";
      return e.blocked.includes(opt.to) ? "Blocked" : "Allowed";
    }
    if (branch === "All Branches") {
      if (!names.length) return "Allowed";
      const cnt = names.filter((n) =>
        opt.adminOnly ? !entry(perms, n).allowed.includes(opt.to) : entry(perms, n).blocked.includes(opt.to)
      ).length;
      return cnt === names.length ? "Blocked" : cnt === 0 ? "Allowed" : "Partly Blocked";
    }
    const e = entry(perms, branch);
    if (opt.adminOnly) return e.allowed.includes(opt.to) ? "Allowed" : "Blocked";
    return e.blocked.includes(opt.to) ? "Blocked" : "Allowed";
  };

  const applyToggle = (e, opt, block) => {
    const to = opt.to;
    if (opt.adminOnly) {
      return {
        blocked: e.blocked.filter((x) => x !== to),
        allowed: block ? e.allowed.filter((x) => x !== to) : [...new Set([...e.allowed, to])],
      };
    }
    return {
      blocked: block ? [...new Set([...e.blocked, to])] : e.blocked.filter((x) => x !== to),
      allowed: e.allowed.filter((x) => x !== to),
    };
  };

  const toggle = async (opt) => {
    const state = optState(opt);
    const block = state !== "Blocked";
    if (userId) {
      const prev = entry(userPerms, userId);
      const next = applyToggle(prev, opt, block);
      setUserPerms((p) => ({ ...p, [userId]: next }));
      try {
        await api.put(`/admin/user-permissions/${encodeURIComponent(userId)}`, next);
        toast.success(`${opt.label} ${block ? "blocked" : "unblocked"} for user ${userId}`);
      } catch (e2) {
        setUserPerms((p) => ({ ...p, [userId]: prev }));
        toast.error(formatApiError(e2.response?.data?.detail));
      }
      return;
    }
    if (branch === "All Branches") {
      const prev = { ...(perms || {}) };
      const next = { ...(perms || {}) };
      names.forEach((n) => { next[n] = applyToggle(entry(prev, n), opt, block); });
      setPerms(next);
      try {
        await Promise.all(names.map((n) => api.put(`/admin/branch-permissions/${encodeURIComponent(n)}`, next[n])));
        toast.success(`${opt.label} ${block ? "blocked" : "unblocked"} for ALL branches`);
      } catch (e2) {
        setPerms(prev);
        toast.error(formatApiError(e2.response?.data?.detail));
      }
      return;
    }
    const prev = entry(perms, branch);
    const next = applyToggle(prev, opt, block);
    setPerms((p) => ({ ...p, [branch]: next }));
    try {
      await api.put(`/admin/branch-permissions/${encodeURIComponent(branch)}`, next);
      toast.success(`${opt.label} ${block ? "blocked" : "unblocked"} for ${branch}`);
    } catch (e2) {
      setPerms((p) => ({ ...p, [branch]: prev }));
      toast.error(formatApiError(e2.response?.data?.detail));
    }
  };

  const scopeLabel = userId ? `user ${userId}` : branch || "—";
  const blockedCount = BLOCKABLE_OPTIONS.filter((o) => optState(o) === "Blocked").length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="h-[92vh] max-h-[92vh] w-[96vw] overflow-hidden sm:max-w-[900px]" data-testid="branch-options-dialog">
        <div className="flex h-full flex-col gap-3 overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-display">Options — Block & Unblock</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Branch:</span>
            {["All Branches", ...names].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => { setBranch(n); setUserId(""); }}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${!userId && branch === n ? "border-blue-600 bg-blue-600 text-white" : "border-border bg-card text-slate-600 hover:border-blue-300"}`}
                data-testid={`perm-branch-${n}`}
              >
                {n}
              </button>
            ))}
            <span className="ml-auto rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700" data-testid="perm-blocked-count">
              {blockedCount} blocked for {scopeLabel}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">User ID:</span>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className={`h-8 rounded-md border px-2 text-xs font-semibold ${userId ? "border-blue-600 bg-blue-50 text-blue-800" : "border-border bg-card text-slate-600"}`}
              data-testid="perm-user-select"
            >
              <option value="">— select a user id —</option>
              {staffIds.map((id) => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
            {userId && (
              <button type="button" onClick={() => setUserId("")} className="text-xs font-semibold text-blue-600 underline" data-testid="perm-user-clear">
                back to branch mode
              </button>
            )}
            <span className="text-[11px] text-muted-foreground">{userId ? "Changes apply only to this user id (on top of their branch rules)." : "Or pick a user id to block/unblock options for one login only."}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Left side: all options. Right side: Block / Unblock for <b>{scopeLabel}</b> (staff logins only — admins always see everything). <span className="text-amber-700">Admin-only</span> options are blocked for staff by default — Unblock to grant access.
          </p>
          {!perms || !userPerms ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="flex-1 overflow-y-auto rounded-lg border border-border">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50">
                  <tr>
                    <th className="border-b border-border px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500">Option</th>
                    <th className="w-[130px] border-b border-border px-3 py-2 text-center text-[10px] uppercase tracking-wider text-slate-500">Status</th>
                    <th className="w-[150px] border-b border-border px-3 py-2 text-center text-[10px] uppercase tracking-wider text-slate-500">Block / Unblock</th>
                  </tr>
                </thead>
                <tbody>
                  {GROUPED_OPTIONS.map((g) => (
                    <Fragment key={g.heading || "top"}>
                      {g.heading && (
                        <tr className="bg-slate-100/80">
                          <td colSpan={3} className="px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{g.heading}</td>
                        </tr>
                      )}
                      {g.items.map((opt) => {
                        const state = optState(opt);
                        const blocked = state === "Blocked";
                        return (
                          <tr key={opt.to} className="hover:bg-slate-50" data-testid={`perm-row-${opt.testid}`}>
                            <td className="border-t border-border px-3 py-1.5 font-medium">
                              {opt.label}
                              {opt.adminOnly && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Admin-only</span>}
                            </td>
                            <td className="border-t border-border px-3 py-1.5 text-center">
                              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${blocked ? "bg-red-50 text-red-700" : state === "Partly Blocked" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`perm-status-${opt.testid}`}>
                                {state}
                              </span>
                            </td>
                            <td className="border-t border-border px-3 py-1.5 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                className={`h-7 px-3 text-xs font-semibold ${blocked ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-red-300 text-red-600 hover:bg-red-50"}`}
                                onClick={() => toggle(opt)}
                                disabled={!userId && !branch}
                                data-testid={`perm-toggle-${opt.testid}`}
                              >
                                {blocked ? "Unblock" : "Block"}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const waApprovalLink = (u) => {
  const digits = String(u.phone || "").replace(/\D/g, "");
  if (!digits) return "";
  const target = digits.length === 10 ? "91" + digits : digits;
  const msg = `Hello ${u.name || ""},\n\nYour NTC login has been APPROVED ✅\n\nUser ID: ${u.user_id || u.email}\nBranch: ${u.office || "—"}\n\nYou can now sign in at ${window.location.origin}\n\n— Nisha Transport Corporation`;
  return `https://wa.me/${target}?text=${encodeURIComponent(msg)}`;
};

function AdminVerifyGate({ onVerified }) {
  const [uid, setUid] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!uid.trim() || !pwd) { setErr("Enter your User ID and password"); return; }
    setBusy(true);
    setErr("");
    try {
      await api.post("/admin/verify", { user_id: uid, password: pwd });
      onVerified();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || "Verification failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="admin-verify-gate">
      <form onSubmit={submit} className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600"><Lock className="h-5 w-5" /></span>
          <div>
            <h2 className="font-display text-base font-bold">Admin Panel Security</h2>
            <p className="text-xs text-muted-foreground">Re-enter your User ID and password to continue.</p>
          </div>
        </div>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>User ID</Label>
            <Input value={uid} onChange={(e) => setUid(e.target.value)} placeholder="Enter your User ID" autoFocus data-testid="admin-verify-userid" />
          </div>
          <div className="grid gap-1.5">
            <Label>Password</Label>
            <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Enter your password" data-testid="admin-verify-password" />
          </div>
          {err && <p className="text-xs font-medium text-red-600" data-testid="admin-verify-error">{err}</p>}
          <Button type="submit" disabled={busy} data-testid="admin-verify-btn">
            {busy ? "Verifying…" : "Unlock Admin Panel"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function AdminPanel() {
  const { user: me } = useAuth();
  const [verified, setVerified] = useState(false);
  const [users, setUsers] = useState(null);
  const [branches, setBranches] = useState(null);
  const [busy, setBusy] = useState("");
  const [justApproved, setJustApproved] = useState(null);
  const [newBranch, setNewBranch] = useState("");
  const [addingBranch, setAddingBranch] = useState(false);
  const [permOpen, setPermOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    api.get("/admin/branches").then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
  }, []);
  useEffect(() => { if (verified) load(); }, [verified, load]);

  const addBranch = async () => {
    const name = newBranch.trim();
    if (!name) { toast.error("Enter a branch name"); return; }
    setAddingBranch(true);
    try {
      await api.post("/admin/branches", { name });
      toast.success(`Branch "${name}" created`);
      setNewBranch("");
      load();
      loadBranches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAddingBranch(false);
    }
  };

  const renameBranch = async (b) => {
    const name = window.prompt(`Rename branch "${b.branch}" to:`, b.branch);
    if (!name || !name.trim() || name.trim() === b.branch) return;
    setBusy(b.branch);
    try {
      const res = await api.put(`/admin/branches/${encodeURIComponent(b.branch)}/rename`, { new_name: name.trim() });
      const n = Object.values(res.data.updated || {}).reduce((a, c) => a + c, 0);
      toast.success(`Renamed to "${res.data.branch}"${n ? ` — ${n} record(s) updated` : ""}`);
      load();
      loadBranches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const removeBranch = async (b) => {
    if (!window.confirm(`Remove branch "${b.branch}"?`)) return;
    setBusy(b.branch);
    try {
      await api.delete(`/admin/branches/${encodeURIComponent(b.branch)}`);
      toast.success(`Branch "${b.branch}" removed`);
      load();
      loadBranches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const setApproval = async (u, approved) => {
    setBusy(u.id);
    try {
      await api.put(`/users/${u.id}/approval`, { approved });
      toast.success(approved ? `${u.name || u.email} approved` : `${u.name || u.email} blocked from login`);
      if (approved) setJustApproved(u);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const setBranchBlock = async (b, blocked) => {
    setBusy(b.branch);
    try {
      await api.put(`/admin/branches/${b.branch}`, { blocked });
      toast.success(blocked ? `${b.branch} branch blocked` : `${b.branch} branch approved`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const pending = (users || []).filter((u) => u.approved === false);
  const approvedUsers = (users || []).filter((u) => u.approved !== false && u.id !== me?.id);

  if (!verified) {
    return (
      <div data-testid="admin-panel-page">
        <PageHeader title="Admin Panel" subtitle="Approve new user logins and control branch access." />
        <AdminVerifyGate onVerified={() => { setVerified(true); setPermOpen(true); }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-panel-page">
      <PageHeader title="Admin Panel" subtitle="Approve new user logins and control branch access." />

      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <ShieldCheck className="h-4 w-4 text-blue-600" /> New User Approvals
        {pending.length > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700" data-testid="pending-count">{pending.length} pending</span>}
      </div>
      {justApproved && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5" data-testid="just-approved-banner">
          <span className="text-sm text-emerald-800"><b>{justApproved.name || justApproved.email}</b> approved ✓ — inform them they can sign in now:</span>
          {waApprovalLink(justApproved) ? (
            <a href={waApprovalLink(justApproved)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700" data-testid="wa-just-approved-link">
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp {justApproved.name || ""}
            </a>
          ) : (
            <span className="text-xs text-emerald-700">(no mobile number saved for this user)</span>
          )}
          <button type="button" onClick={() => printStaffWelcomeCard(justApproved, window.location.origin)} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-600 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100" data-testid="welcome-card-btn">
            <IdCard className="h-3.5 w-3.5" /> Welcome Card
          </button>
          <button type="button" className="ml-auto text-xs text-emerald-700 hover:underline" onClick={() => setJustApproved(null)} data-testid="dismiss-approved-banner">Dismiss</button>
        </div>
      )}
      <TableShell>
        {users === null ? (
          <div className="space-y-2 p-4">{[1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : pending.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="no-pending-users">No users waiting for approval. New logins created in NTC Staff Logins will appear here.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>User ID</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending.map((u) => (
                <TableRow key={u.id} data-testid="pending-user-row">
                  <TableCell className="text-sm font-medium">{u.name || "—"}</TableCell>
                  <TableCell className="text-sm">{u.user_id || "—"}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell className="text-sm">{u.office || "—"}</TableCell>
                  <TableCell className="text-sm capitalize">{u.role}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button type="button" title="Welcome card" onClick={() => printStaffWelcomeCard(u, window.location.origin)} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-blue-600 hover:bg-blue-50" data-testid="pending-welcome-card-btn">
                        <IdCard className="h-4 w-4" />
                      </button>
                      {waApprovalLink(u) ? (
                        <a href={waApprovalLink(u)} target="_blank" rel="noreferrer" title="Send approval message on WhatsApp" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50" data-testid="wa-pending-user-link">
                          <MessageCircle className="h-4 w-4" />
                        </a>
                      ) : (
                        <span title="No mobile number saved" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-300"><MessageCircle className="h-4 w-4" /></span>
                      )}
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy === u.id} onClick={() => setApproval(u, true)} data-testid="approve-user-btn">
                        <CheckCircle2 className="mr-1 h-4 w-4" /> Approve
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableShell>

      <div className="mb-2 mt-8 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-700">
        <Building2 className="h-4 w-4 text-blue-600" /> Branch Access
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setPermOpen(true)} data-testid="branch-options-btn">
            <ShieldCheck className="mr-1 h-4 w-4" /> Branch Options Access
          </Button>
          <Input className="h-9 w-[200px] text-sm" placeholder="New branch name" value={newBranch} onChange={(e) => setNewBranch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addBranch()} data-testid="new-branch-input" />
          <Button size="sm" onClick={addBranch} disabled={addingBranch} data-testid="add-branch-btn">
            <Plus className="mr-1 h-4 w-4" /> Add Branch
          </Button>
        </div>
      </div>
      <TableShell>
        {branches === null ? (
          <div className="space-y-2 p-4">{[1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Branch</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {branches.map((b) => (
                <TableRow key={b.branch} data-testid={`branch-row-${b.branch}`}>
                  <TableCell className="text-sm font-medium">{b.branch}{b.custom && <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600">Custom</span>}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${b.blocked ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`branch-status-${b.branch}`}>
                      {b.blocked ? "Blocked" : "Approved"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {b.blocked ? (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy === b.branch} onClick={() => setBranchBlock(b, false)} data-testid={`approve-branch-${b.branch}`}>
                          <CheckCircle2 className="mr-1 h-4 w-4" /> Unblock
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" disabled={busy === b.branch} onClick={() => setBranchBlock(b, true)} data-testid={`block-branch-${b.branch}`}>
                          <Ban className="mr-1 h-4 w-4" /> Block
                        </Button>
                      )}
                      {b.custom && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600" disabled={busy === b.branch} onClick={() => renameBranch(b)} title="Rename custom branch" data-testid={`rename-branch-${b.branch}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {b.custom && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-red-600" disabled={busy === b.branch} onClick={() => removeBranch(b)} title="Remove custom branch" data-testid={`delete-branch-${b.branch}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableShell>
      <p className="mt-2 text-xs text-muted-foreground">Blocking a branch stops all its staff from signing in. Admin accounts are never blocked. {approvedUsers.length} approved user(s) in total.</p>
      <BranchOptionsDialog open={permOpen} onClose={() => setPermOpen(false)} branches={branches} users={users} />
    </div>
  );
}
